import React, {useEffect, useMemo, useRef, useState} from 'react';
import _isEqual from 'lodash/isEqual';

import {ModalReact, ModalType} from 'common/components/modal';
import {type Button, ButtonReact} from 'common/components/button';
import {useReportLayoutManager} from 'common/contexts/react/report-layout-manager-context';
import {Segmentation} from 'common/types/reports/query';
import {PropertyType} from 'common/report/constants';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {SvgIconType} from 'common/components/svg-icon/types';
import {useTranslation} from 'common/i18n/hooks';
import {propertiesUtils} from 'common/report/util/properties';
import {ControlBarReact} from 'common/widgets/control-bar';
import {SwitchEl} from 'common/components/switch';
import {Bucket, StringBucketType} from 'common/widgets/bucket-pane/types';
import {useStagedState} from 'common/hooks/useStagedState';
import {DateBucketPaneReact, DateBucketPaneTimeGroup} from 'common/widgets/bucket-pane/date-bucket-pane';
import {isSegmentEntry, QueryBucketableEntry} from 'common/query-builder/query-entry-section/types';
import {getCustomBucketAttributes} from 'common/query-builder/query-entry-section/util/group-data';
import {useQueryContextManager} from 'common/contexts/react/query-context-manager';
import {NumericBucketPaneReact} from 'common/widgets/bucket-pane/numeric-bucket-pane';
import {ToggleReact} from 'common/components/toggle';
import {SelectEvent, ToggleTheme} from 'common/components/toggle/types';
import {usePropertyEvents} from 'common/hooks/usePropertyEvents';
import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';

import styles from './index.module.styl';
// eslint-disable-next-line import/no-cycle -- string buckets render qes currently
import {StringBuckets} from './types/string';
import {TextAnalysisTrackingEvent} from './types';

export function GroupDataSidebar(props: {
  entry: QueryBucketableEntry;
  eventNames: string[];
  entryIdx: number;
  propertyType: PropertyType;
  propertyIcon: SvgIconType;
  onClose: () => void;
  onUpdate: (segment: Segmentation) => void;
  dispatchTrack: (event: string, props?: Record<string, any>) => void;
}) {
  const isTextAnalysisEnabled = useFeatureRollout(FeatureRolloutName.TextAnalysis);
  const queryContextManager = useQueryContextManager();

  const bucketAttributes = getCustomBucketAttributes({
    entry: props.entry,
    eventNames: props.eventNames || [],
    defaultBucket: isSegmentEntry(props.entry)
      ? queryContextManager.segmentationMetadatas?.[props.entryIdx]?.numericBucket
      : undefined,
  });

  const property = props.entry.event as Segmentation;

  const propertyEvents = usePropertyEvents(
    {
      name: property?.propertyName as string,
    },
    {
      enabled:
        !!property &&
        !property.customPropertyId &&
        !property.customProperty &&
        typeof property.propertyName === `string`,
    },
  );

  const likelyEvent = propertyEvents.data?.find((event) => props.eventNames.includes(event.name));

  const [segment, setSegment] = useStagedState(property, props.onUpdate);
  const [isTextAnalysis, setIsTextAnalysis] = useState(property.customGroups?.type === StringBucketType.TextAnalysis);

  const [validBucket, setValidBucket] = useState(true);

  const submitButton = useRef<Button | null>(null);

  const i18n = useTranslation();
  const {t} = i18n;

  const reportLayoutManager = useReportLayoutManager();

  const hasChanges = useMemo(() => {
    return !_isEqual(segment, property);
  }, [segment, property]);

  const hasCustomBucket = !!segment.customBucket || !!segment.customGroups;

  const propertyName = propertiesUtils.renameProperty(property.propertyName, property.resourceType, i18n);

  const supportsTextAnalysis = isTextAnalysisEnabled && props.propertyType === PropertyType.String;

  const applyBucketSelections = () => {
    if (isTextAnalysis) {
      props.dispatchTrack(TextAnalysisTrackingEvent.GroupsApplied);
    }
    setSegment(segment, true);
  };

  const onKeyUp = (ev: React.KeyboardEvent) => {
    if (ev.key === `Enter` && validBucket) {
      applyBucketSelections();
    }
  };

  const onClickReset = () => {
    setSegment(
      {
        ...segment,
        customBucket: undefined,
        customGroups: undefined,
      },
      true,
    );
  };

  const handleBucketUpdate = ({bucket, isValid}: {bucket: Bucket; isValid: boolean}) => {
    setSegment({
      ...segment,
      customBucket: bucket,
    });
    setValidBucket(isValid);
  };

  const handleUnitUpdate = ({
    focusSubmit,
    isValid,
    unit,
  }: {
    focusSubmit: boolean;
    isValid: boolean;
    unit: DateBucketPaneTimeGroup;
  }) => {
    focusSubmit && submitButton.current?.focus?.();
    if (segment) {
      setSegment({
        ...segment,
        unit: unit as Segmentation[`unit`],
      });
    }
    setValidBucket(isValid);
  };

  const segmentParams = (() => {
    let params: {segment: Segmentation | null; listProperty?: Segmentation} = {segment};

    if (segment?.listItemGroup) {
      // For list of objects, we pass segment.listItemGroup as the segment (and include the outer customGroups).
      // listProperty is also needed so the appropriate values query can be made using the listProperty
      params = {
        segment: {
          ...segment.listItemGroup,
          ...(segment.customGroups ? {customGroups: segment.customGroups} : {}),
        },
        listProperty: segment,
      };
    }

    return params;
  })();

  useEffect(() => {
    if (isTextAnalysis) {
      props.dispatchTrack(TextAnalysisTrackingEvent.SidebarOpened, {
        // if customGroups.type is text analysis, it means we're opening the text analysis tab to edit existing text analysis groups.
        // otherwise, it means we're switching from standard groups tab to the text analysis tab.
        'Is Editing': property.customGroups?.type === StringBucketType.TextAnalysis,
      });
    }
  }, [isTextAnalysis]);

  return (
    <ModalReact tabIndex={-1} hideBackdrop hideCloseButton modalType={ModalType.RightDrawer} open>
      <div
        className={styles.container}
        style={{
          width: reportLayoutManager.getUserSidebarWidth(),
        }}
      >
        <div className={styles.header}>
          <ButtonReact icon='arrow-left' theme={ButtonTheme.Tertiary} onClick={props.onClose} />
          <h2 className={styles.headerText}>{t(`query-builder.actions.group-data`)}</h2>
          <SvgIconReact className={styles.headerIcon} icon='caret-right' />
          <SvgIconReact className={styles.headerIcon} icon={props.propertyIcon} />
          <span className={styles.headerText}>{propertyName}</span>
          {isTextAnalysis && (
            <ControlBarReact
              className={styles.headerOverflow}
              theme={ButtonTheme.Tertiary}
              overflowIcon='ellipsis'
              overflowMenuHeight={0}
              overflowMenuWidth={240}
              overflowControls={[
                {
                  items: [
                    {
                      label: t(`query-builder.buckets.mutually-exclusive`),
                      isDisabled: true,
                      actionsRenderer: () =>
                        SwitchEl({
                          attrs: {
                            disabled: true,
                            on: true,
                          },
                        }),
                    },
                    {
                      label: t(`query-builder.buckets.group-remaining`),
                      isDisabled: true,
                      actionsRenderer: () =>
                        SwitchEl({
                          attrs: {
                            disabled: true,
                            on: false,
                          },
                        }),
                    },
                  ],
                },
              ]}
            />
          )}
        </div>
        <div className={styles.content} onKeyUp={onKeyUp}>
          {supportsTextAnalysis && (
            <ToggleReact
              selected={isTextAnalysis ? `text-analysis` : `standard`}
              className={styles.fullWidth}
              theme={ToggleTheme.TabBar}
              options={[
                {
                  label: `Standard`,
                  value: `standard`,
                  icon: `type-text`,
                },
                {
                  label: `Text Analysis`,
                  value: `text-analysis`,
                  icon: `spark`,
                },
              ]}
              onSelect={(ev) => setIsTextAnalysis((ev as SelectEvent<string>).detail.selected === `text-analysis`)}
            />
          )}
          {(props.propertyType === PropertyType.String || props.propertyType === PropertyType.List) &&
            segmentParams.segment && (
              <StringBuckets
                eventNames={props.eventNames || []}
                likelyEvent={likelyEvent}
                isTextAnalysis={isTextAnalysis}
                segment={segmentParams.segment}
                listProperty={segmentParams.listProperty}
                onUpdate={(newSegment, isValid) => {
                  if (segment?.listItemGroup) {
                    // For list of objects, the segment passed to `StringBucketPaneEl` is segment.listItemGroup, so
                    // we use the emitted segment to update segment.listItemGroup
                    const {customGroups, ...listItemGroup} = newSegment;
                    newSegment = {...segment, listItemGroup, customGroups};
                  }
                  setSegment(newSegment);
                  setValidBucket(isValid);
                }}
                dispatchTrack={props.dispatchTrack}
              />
            )}
          {props.propertyType === PropertyType.Number && (
            <NumericBucketPaneReact
              className={styles.fullWidth}
              useNewDesign
              {...bucketAttributes}
              selectedBucket={segment.customBucket || bucketAttributes.selectedBucket}
              onUpdate={handleBucketUpdate}
            />
          )}
          {props.propertyType === PropertyType.Datetime && (
            <DateBucketPaneReact onUpdate={handleUnitUpdate} unit={segment.unit as DateBucketPaneTimeGroup} />
          )}
        </div>
        <div className={styles.footer}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={onClickReset} disabled={!hasCustomBucket}>
            {t(`common.reset`)}
          </ButtonReact>
          <ButtonReact
            ref={submitButton as any}
            className={styles.applyButton}
            theme={ButtonTheme.Primary}
            onClick={applyBucketSelections}
            disabled={!hasChanges || !validBucket}
          >
            {t(`query-builder.buckets.apply-groups`)}
          </ButtonReact>
        </div>
      </div>
    </ModalReact>
  );
}
